
















import { defineComponent, PropType, computed, ref } from '@vue/composition-api';
import { useContext } from '@berlin-front/common/composables/context';
import Vue from 'vue';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import { bemx, pageTop } from '@berlin-front/common/utils';
import useCss from '@berlin-front/common/composables/useCss';

/**
 * ## 選択フィールド
 *
 * モジュール内の標準的な選択フィールド（プルダウン）を扱うコンポーネント。
 * 選択要素（`value`）はv-modelを使いv-selectと値を連動させること。
 *
 * Vuetifyの`v-select`については以下を参照。
 *
 * - 仕様書：https://vuetifyjs.com/ja/api/v-select/
 * - コード：https://github.com/vuetifyjs/vuetify/tree/master/packages/vuetify/src/components/VSelect
 *
 */
export default defineComponent({
  name: 'AppSelect',

  props: {
    /**
     * 選択項目
     *
     * 選択フィールドのプルダウンに展開される複数の項目。
     */
    items: {
      type: Array as PropType<Readonly<(string | number)[]>>,
      required: true,
    },

    /**
     * ラベル。
     *
     * テキストフィールドの項目ラベルとして使用される。
     * 正式なフィールド名でなく、簡易的な名称をラベルに設定しても問題なし。
     * また、テキストフィールドによってはラベル名の記載がないものもある。
     */
    label: {
      type: String,
      default: '',
    },

    /**
     * 選択した値
     *
     * v-modelで設定した値が連動される。
     */
    value: {
      type: [String, Number] as PropType<string | number>,
      required: true,
    },

    /**
     * 選択項目の値から、表示テキストを作成する関数。
     */
    valueRender: {
      type: Function as PropType<(val: string | number) => string>,
      default: (val: string | number) => `${val.toString()}`,
    },

    /**
     * エラーメッセージ
     */
    errorMessages: {
      type: Array as PropType<string[]>,
      default: () => [] as string[],
    },

    /**
     * エラーメッセージやインフォメーションメッセージを非表示にする。
     *
     * 非表示になるのはメッセージのみで、エラーカラーの適応はされる。
     */
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    /**
     * inputイベント
     */
    'input',
    /**
     * blurイベント
     */
    'blur',
  ],

  setup(props, { emit }) {
    // v-modelと連動
    const selectedValue = computed({
      get: () => props.value,
      set: (val) => emit('input', val),
    });

    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);

    const className = bemx('AppSelect');

    const vSelectItems = computed(() =>
      props.items.map((v) => ({
        value: v,
        text: props.valueRender(v),
      })),
    );

    // start: メニューの座標制御。
    const isFocused = ref(false);
    const isFade = ref(false);
    const menuTop = ref(0);
    const select = ref<Vue>();
    useCss(() => {
      if (!isFocused.value && !isFade.value) {
        return ``;
      }

      return `
        .v-menu__content.theme--light {
          top: ${menuTop.value}px!important;
        }
      `;
    });
    const transitionEndHandlers: (() => void)[] = [];
    const onTransitionEnd = () => {
      transitionEndHandlers.splice(0).forEach((handler) => handler());
    };
    const onFocus = () => {
      onTransitionEnd();
      isFocused.value = true;
      if (select.value?.$el instanceof HTMLElement) {
        menuTop.value = pageTop(select.value.$el);
      }
    };
    const onBlur = () => {
      emit('blur');
      isFocused.value = false;
      isFade.value = true;
      const elem = document.querySelector('.v-menu__content');
      if (elem instanceof HTMLElement) {
        transitionEndHandlers.push(() => {
          isFade.value = false;
          elem.removeEventListener('transitionend', onTransitionEnd);
        });
        elem.addEventListener('transitionend', onTransitionEnd);
        window.setTimeout(onTransitionEnd, 1000);
      }
    };
    // end: メニューの座標制御。

    return {
      onFocus,
      onBlur,
      className,
      primaryColor,
      selectedValue,
      vSelectItems,
      select,
    };
  },
});
